// framework/client/helpers/MessagingClient/GroupSettingsModal.js
import { ComponentBase } from "../../../core/ComponentBase.js";
import { createAvatarElement } from "../avatarHelper.js";


export class GroupSettingsModal extends ComponentBase {
    static styles = `
        .group-settings-modal .modal-dialog {
            max-width: 600px;
        }
        .group-settings-modal .modal-body {
            max-height: 75vh;
            overflow-y: auto;
        }
        .group-avatar-preview {
            width: 100px;
            height: 100px;
            border-radius: 50%;
            object-fit: cover;
            margin-bottom: 1rem;
            cursor: pointer;
            border: 2px solid var(--bs-border-color);
        }
        .participant-list-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0.75rem; /* Adjusted padding */
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }
        .participant-list-item:last-child {
            border-bottom: none;
        }
        .participant-info {
            display: flex;
            align-items: center;
            flex-grow: 1; /* Allow name to take space */
            overflow: hidden; /* For text truncation */
        }
        .participant-avatar-small {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-color: var(--bs-secondary);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            margin-right: 0.75rem;
            flex-shrink: 0;
        }
        .participant-avatar-small img {
            width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
        }
        .participant-name {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .participant-role-badge {
            font-size: 0.7rem;
            margin-left: 0.5rem;
        }
        .participant-actions .btn, .participant-actions .dropdown-toggle {
            padding: 0.1rem 0.3rem; /* Smaller buttons */
            font-size: 0.8rem;
        }
        .add-participants-container {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid var(--bs-border-color);
        }
        .search-results-for-add {
            max-height: 150px;
            overflow-y: auto;
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--bs-border-radius);
        }
        .delete-group-btn-container, .leave-group-btn-container {
            margin-top: 2rem;
            padding-top: 1rem;
            border-top: 1px solid var(--bs-border-color);
        }
        .delete-group-btn-container {
            border-top-color: var(--bs-danger);
        }
    `;

    constructor(config) {
        super(config);
        this.channelId = this.config.channelId;
        this.myContactId = this.config.myContactId;
        this.currentProjectId = this.config.currentProjectId;

        this.channelData = null;
        this.myRoleInGroup = null;
        this.allProjectContacts = []; // To store contacts for adding members
        this.addMembersSearchTerm = '';
        this.addMembersSearchResults = [];

        this.modalElement = null;
        this.modalInstance = null;
        this.avatarFileUpload = null;
        this.addParticipantsSearchInput = null;
        this.addParticipantsResultsContainer = null;

        this.applyStyles();
    }

    async initialize() {
        await super.initialize();
        this.createModalElement();

        this.avatarFileUpload = await this.createManagedComponent('FileUpload', null, {
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-avatar-upload`,
            getUploadKey: async () => {
                if (!this.channelId) return null;
                return await this.appClient.rpcClient.call('MessagingRpc.getChannelAvatarUploadKey', { channelId: this.channelId }, { projectId: this.currentProjectId });
            },
            allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif'],
            maxFiles: 1, maxFileSize: 2 * 1024 * 1024, showLabel: false, hidden: true
        });
        this.avatarFileUpload.on('uploadCompleted', this.handleAvatarUploaded.bind(this));
    }

    createModalElement() {
        // ... (same as before, ensure modal-body has an ID for easier targeting)
        this.modalElement = document.createElement('div');
        this.modalElement.className = 'modal fade group-settings-modal';
        this.modalElement.id = `${this.config.instanceId}-modal`;
        this.modalElement.setAttribute('tabindex', '-1');
        this.modalElement.innerHTML = `
            <div class="modal-dialog modal-dialog-scrollable"> <!-- Added modal-dialog-scrollable -->
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" data-i18n="Group Settings">Group Settings</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" id="${this.config.instanceId}-modal-body">
                        <!-- Content will be rendered here -->
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" data-i18n="Close">Close</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(this.modalElement);
        this.modalInstance = new bootstrap.Modal(this.modalElement);
        this.modalElement.addEventListener('hidden.bs.modal', () => this.emit('closed'));
    }

    async show() {
        if (!this.channelId || !this.currentProjectId) {
            this.log.error("Cannot show GroupSettingsModal: channelId or currentProjectId missing.");
            return;
        }
        await this.loadAllDataForModal();
        await this.renderModalContent(); // Await this
        this.modalInstance.show();
    }

    hide() {
        if (this.modalInstance) {
            this.modalInstance.hide();
        }
        this.emit('closed');
    }


    async loadAllDataForModal() {
        try {
            // Fetch channel data
            this.channelData = await this.appClient.rpcClient.call('MessagingRpc.getChannelById', { channelId: this.channelId }, { projectId: this.currentProjectId });
            if (this.channelData) {
                const myMemberInfo = this.channelData.members.find(m => m.contactId === this.myContactId);
                this.myRoleInGroup = myMemberInfo ? myMemberInfo.role : null;
            } else {
                throw new Error("Channel data not found.");
            }

            // Fetch all project contacts for member management
            const contactsResult = await this.appClient.rpcClient.call('MessagingContactsRpc.getData',
                { filter: { projectId: this.currentProjectId, isArchived: { $ne: true } }, pageSize: 1000 }, // Fetch all non-archived
                { projectId: this.currentProjectId }
            );
            this.allProjectContacts = contactsResult.data || [];

        } catch (error) {
            this.log.error("Error loading data for GroupSettingsModal:", error);
            this.appClient.notifier.error("Failed to load group details or contacts.");
            if (this.modalInstance) {
                this.modalInstance.hide();
            }
        }
    }

    async refreshModalData() {
        await this.loadAllDataForModal();
        await this.renderModalContent(); // Await this
    }


    async renderModalContent() {
        const modalBody = this.modalElement.querySelector(`#${this.config.instanceId}-modal-body`);
        modalBody.innerHTML = '';

        if (!this.channelData) { /* ... */ return; }
        const canEditInfo = this.myRoleInGroup === 'admin';
        const infoSection = document.createElement('div');
        infoSection.className = 'mb-3 text-center';

        const groupAvatarElement = createAvatarElement({
            displayName: this.channelData.name,
            avatarUrl: this.channelData.avatarUrl, // Relative path
            sizeClass: 'large', // Defined in your CSS
            additionalClasses: ['group-avatar-preview'] // Keep your specific class if needed
        });
        if (canEditInfo) {
            groupAvatarElement.addEventListener('click', () => {
                if (this.avatarFileUpload) {
                    this.avatarFileUpload.openFile();
                }
            });
        }
        infoSection.appendChild(groupAvatarElement);
        if (this.avatarFileUpload) {
            const fileUploadElement = await this.avatarFileUpload.render();
            infoSection.appendChild(fileUploadElement); 
        }
        const nameInputContainer = this.createEditableField('groupName', 'Group Name', this.channelData.name, canEditInfo, 'text');
        infoSection.appendChild(nameInputContainer);
        const topicTextareaContainer = this.createEditableField('groupTopic', 'Group Topic', this.channelData.topic || '', canEditInfo, 'textarea');
        infoSection.appendChild(topicTextareaContainer);
        if (canEditInfo) {
            const saveInfoButton = document.createElement('button');
            saveInfoButton.className = 'btn btn-sm btn-primary mt-2';
            saveInfoButton.textContent = this.t('Save Info Changes');
            saveInfoButton.setAttribute('data-i18n', 'Save Info Changes');
            saveInfoButton.addEventListener('click', this.handleSaveInfo.bind(this));
            infoSection.appendChild(saveInfoButton);
        }
        modalBody.appendChild(infoSection);


        // --- Participants Section ---
        const participantsSection = document.createElement('div');
        participantsSection.className = 'mb-3';
        participantsSection.innerHTML = `<h6 data-i18n="Participants">Participants (${this.channelData.members.length})</h6>`;
        const participantsList = document.createElement('div');
        participantsList.className = 'list-group list-group-flush';


        this.channelData.members.forEach(member => {
            const contactDetails = this.allProjectContacts.find(c => c.contactId === member.contactId);
            const displayName = contactDetails?.displayName || member.contactId;
            const participantAvatarElement = createAvatarElement({
                displayName: displayName,
                avatarUrl: contactDetails?.avatarUrl, // Relative path from contactDetails
                sizeClass: 'small',
                additionalClasses: ['participant-avatar-small'] // Keep specific class if it has unique styles
            });

            const item = document.createElement('div');
            item.className = 'participant-list-item list-group-item';
            item.style.cursor = 'pointer';
            item.setAttribute('data-action-view-contact', member.contactId);

            let memberActionsHtml = '';
            if (this.myRoleInGroup === 'admin' && member.contactId !== this.myContactId) {
                memberActionsHtml = `
                    <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            <i class="bi bi-three-dots"></i>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                            <li><a class="dropdown-item" href="#" data-action="remove-participant" data-contact-id="${member.contactId}">${this.t('Remove')}</a></li>
                            ${member.role === 'member'
                        ? `<li><a class="dropdown-item" href="#" data-action="make-admin" data-contact-id="${member.contactId}">${this.t('Make Admin')}</a></li>`
                        : `<li><a class="dropdown-item" href="#" data-action="dismiss-admin" data-contact-id="${member.contactId}">${this.t('Dismiss as Admin')}</a></li>`
                    }
                        </ul>
                    </div>
                `;
            }

            item.innerHTML = `
                <div class="participant-info" data-action-view-contact-info="${member.contactId}"> <!-- Also make info part clickable -->
                    ${participantAvatarElement.outerHTML}
                    <span class="participant-name">${displayName}</span>
                    ${member.role === 'admin' ? `<span class="badge bg-primary participant-role-badge ms-2" data-i18n="Admin">${this.t('Admin')}</span>` : ''}
                </div>
                <div class="participant-actions">
                    ${memberActionsHtml}
                </div>
            `;
            participantsList.appendChild(item);
        });
        participantsSection.appendChild(participantsList);
        modalBody.appendChild(participantsSection);

        participantsList.addEventListener('click', (e) => {
            const actionTarget = e.target.closest('[data-action]');
            const viewContactTarget = e.target.closest('[data-action-view-contact-info]');

            if (actionTarget) { // Prioritize action buttons
                e.preventDefault();
                e.stopPropagation(); // Prevent view-contact if clicking an action button inside
                const action = actionTarget.dataset.action;
                const contactId = actionTarget.dataset.contactId;

                if (action === 'remove-participant') this.handleRemoveParticipant(contactId);
                else if (action === 'make-admin') this.handleUpdateMemberRole(contactId, 'admin');
                else if (action === 'dismiss-admin') this.handleUpdateMemberRole(contactId, 'member');
            } else if (viewContactTarget) { // Then check for view contact info
                e.preventDefault();
                const contactId = viewContactTarget.dataset.actionViewContactInfo;
                if (contactId) {
                    this.emit('contactInfoClicked', { contactId });
                }
            }
        });

        // ... (Add Participants Section, Leave Group, Delete Group - same as before) ...
        if (this.myRoleInGroup === 'admin') {
            const addSection = document.createElement('div');
            addSection.className = 'add-participants-container';
            addSection.innerHTML = `<h6 data-i18n="Add Participants">Add Participants</h6>`;
            this.addParticipantsSearchInput = document.createElement('input');
            this.addParticipantsSearchInput.type = 'search';
            this.addParticipantsSearchInput.className = 'form-control form-control-sm mb-2 contact-search-input';
            this.addParticipantsSearchInput.placeholder = this.t('Search contacts to add...');
            this.addParticipantsSearchInput.setAttribute('data-i18n-placeholder', 'Search contacts to add...');
            this.addParticipantsSearchInput.addEventListener('input', (e) => this.filterContactsForAdding(e.target.value));
            addSection.appendChild(this.addParticipantsSearchInput);
            this.addParticipantsResultsContainer = document.createElement('div');
            this.addParticipantsResultsContainer.className = 'search-results-for-add list-group';
            addSection.appendChild(this.addParticipantsResultsContainer);
            modalBody.appendChild(addSection);
        } else {
            this.addParticipantsSearchInput = null;
            this.addParticipantsResultsContainer = null;
        }
        const leaveSection = document.createElement('div');
        leaveSection.className = 'leave-group-btn-container';
        const leaveButton = document.createElement('button');
        leaveButton.className = 'btn btn-warning w-100';
        leaveButton.textContent = this.t('Leave Group');
        leaveButton.setAttribute('data-i18n', 'Leave Group');
        leaveButton.addEventListener('click', this.handleLeaveGroup.bind(this));
        leaveSection.appendChild(leaveButton);
        modalBody.appendChild(leaveSection);
        if (this.myRoleInGroup === 'admin') {
            const deleteSection = document.createElement('div');
            deleteSection.className = 'delete-group-btn-container';
            const deleteButton = document.createElement('button');
            deleteButton.className = 'btn btn-danger w-100';
            deleteButton.textContent = this.t('Delete Group');
            deleteButton.setAttribute('data-i18n', 'Delete Group');
            deleteButton.addEventListener('click', this.handleDeleteGroup.bind(this));
            deleteSection.appendChild(deleteButton);
            modalBody.appendChild(deleteSection);
        }

        this.appClient.tx.applyTranslations(modalBody);
    }

    filterContactsForAdding(searchTerm) {
        if (!this.addParticipantsResultsContainer) {
            this.log.warn("filterContactsForAdding called before addParticipantsResultsContainer is rendered.");
            return;
        }
        this.addMembersSearchTerm = searchTerm.toLowerCase();
        this.addParticipantsResultsContainer.innerHTML = '';

        if (!this.addMembersSearchTerm) return;

        const memberContactIds = new Set(this.channelData.members.map(m => m.contactId));
        this.addMembersSearchResults = this.allProjectContacts.filter(contact =>
            !memberContactIds.has(contact.contactId) &&
            contact.contactId !== this.myContactId &&
            contact.displayName.toLowerCase().includes(this.addMembersSearchTerm)
        );

        if (this.addMembersSearchResults.length === 0) {
            this.addParticipantsResultsContainer.innerHTML = `<p class="text-muted small p-2">${this.t('No matching contacts found.')}</p>`;
            this.appClient.tx.applyTranslations(this.addParticipantsResultsContainer);
            return;
        }

        this.addMembersSearchResults.forEach(contact => {
            const item = document.createElement('button');
            item.type = 'button';
            item.className = 'contact-list-item list-group-item list-group-item-action';
            const participantAvatarElement = createAvatarElement({
                displayName: contact?.displayName,
                avatarUrl: contact?.avatarUrl, // Relative path from contactDetails
                sizeClass: 'small',
                additionalClasses: ['participant-avatar-small'] // Keep specific class if it has unique styles
            });
            item.innerHTML = `
                <div class="participant-info">
                    ${participantAvatarElement.outerHTML}
                    <span class="participant-name">${contact.displayName}</span>
                </div>
                <button class="btn btn-sm btn-success add-to-group-btn" data-contact-id="${contact.contactId}" data-i18n="Add">Add</button>
            `;
            item.querySelector('.add-to-group-btn').addEventListener('click', (e) => {
                e.stopPropagation();
                this.handleAddParticipant(contact.contactId);
            });
            this.addParticipantsResultsContainer.appendChild(item);
        });
        this.appClient.tx.applyTranslations(this.addParticipantsResultsContainer);
    }


    async handleAddParticipant(contactIdToAdd) {
        try {
            await this.appClient.rpcClient.call('MessagingRpc.addContactToChannel',
                { channelId: this.channelId, contactIdToAdd },
                { projectId: this.currentProjectId }
            );
            this.appClient.notifier.success("Member added to group.");
            await this.refreshModalData(); // Refresh modal to show new member and update search results
            this.addParticipantsSearchInput.value = ''; // Clear search
            this.filterContactsForAdding(''); // Clear results list
        } catch (error) {
            this.log.error("Error adding participant:", error);
            this.appClient.notifier.error(`Failed to add member: ${error.message}`);
        }
    }

    async handleRemoveParticipant(contactIdToRemove) {
        const contact = this.allProjectContacts.find(c => c.contactId === contactIdToRemove);
        const confirmed = await this.appClient.notifier.confirm(
            this.t("Are you sure you want to remove {{displayName}} from the group?", { displayName: contact?.displayName || contactIdToRemove }),
            { title: this.t('Confirm Removal'), type: 'warning' }
        );
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.removeContactFromChannel',
                    { channelId: this.channelId, contactIdToRemove },
                    { projectId: this.currentProjectId }
                );
                this.appClient.notifier.success("Member removed from group.");
                await this.refreshModalData();
            } catch (error) {
                this.log.error("Error removing participant:", error);
                this.appClient.notifier.error(`Failed to remove member: ${error.message}`);
            }
        }
    }

    async handleUpdateMemberRole(contactId, newRole) {
        const contact = this.allProjectContacts.find(c => c.contactId === contactId);
        const actionText = newRole === 'admin' ? this.t('make an admin') : this.t('dismiss as admin');
        const confirmed = await this.appClient.notifier.confirm(
            this.t("Are you sure you want to {{actionText}} {{displayName}}?", { actionText, displayName: contact?.displayName || contactId }),
            { title: this.t('Confirm Role Change') }
        );
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.updateChannelMemberRole',
                    { channelId: this.channelId, targetContactId: contactId, newRole },
                    { projectId: this.currentProjectId }
                );
                this.appClient.notifier.success("Member role updated.");
                await this.refreshModalData();
            } catch (error) {
                this.log.error("Error updating member role:", error);
                this.appClient.notifier.error(`Failed to update role: ${error.message}`);
            }
        }
    }

    async handleLeaveGroup() {
        const confirmed = await this.appClient.notifier.confirm(
            this.t("Are you sure you want to leave this group?"),
            { title: this.t('Confirm Leave Group'), type: 'warning' }
        );
        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.removeContactFromChannel',
                    { channelId: this.channelId, contactIdToRemove: this.myContactId },
                    { projectId: this.currentProjectId }
                );
                this.appClient.notifier.success("You have left the group.");
                // --- FIX HERE ---
                if (this.modalInstance) {
                    this.modalInstance.hide();
                }
                // --- END FIX ---
                this.emit('leftGroup', { channelId: this.channelId });
            } catch (error) {
                this.log.error("Error leaving group:", error);
                this.appClient.notifier.error(`Failed to leave group: ${error.message}`);
            }
        }
    }


    createEditableField(id, label, value, isEditable, type = 'text') {
        const div = document.createElement('div');
        div.className = 'mb-2';
        const labelEl = document.createElement('label');
        labelEl.htmlFor = `${this.config.instanceId}-${id}`;
        labelEl.className = 'form-label visually-hidden';
        labelEl.textContent = this.t(label);
        labelEl.setAttribute('data-i18n', label);
        div.appendChild(labelEl);

        if (isEditable) {
            const inputEl = document.createElement(type === 'textarea' ? 'textarea' : 'input');
            if (type !== 'textarea') {
                inputEl.type = type;
            }
            inputEl.className = 'form-control form-control-sm';
            inputEl.id = `${this.config.instanceId}-${id}`; // Use instanceId for uniqueness
            inputEl.value = value;
            inputEl.placeholder = this.t(label);
            inputEl.setAttribute('data-i18n-placeholder', label);
            if (type === 'textarea') inputEl.rows = 2;
            div.appendChild(inputEl);
        } else {
            const p = document.createElement('p');
            p.className = 'form-control-plaintext form-control-sm text-center';
            p.innerHTML = `<strong data-i18n="${label}">${this.t(label)}:</strong> ${value || `<em>${this.t('Not set')}</em>`}`;
            this.appClient.tx.applyTranslations(p);
            div.appendChild(p);
        }
        return div;
    }

    async handleAvatarUploaded() {
        const uploadKey = this.avatarFileUpload.getValue();
        if (uploadKey) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.updateChannelAvatar',
                    { channelId: this.channelId, uploadKey: uploadKey },
                    { projectId: this.currentProjectId }
                );
                this.appClient.notifier.success("Group avatar updated.");
                await this.refreshModalData();
            } catch (error) {
                this.log.error("Error updating group avatar:", error);
                this.appClient.notifier.error("Failed to update avatar.");
            } finally {
                this.avatarFileUpload.clear();
            }
        }
    }

    async handleSaveInfo() {
        const nameInputEl = this.modalElement.querySelector(`#${this.config.instanceId}-groupName`);
        const topicTextareaEl = this.modalElement.querySelector(`#${this.config.instanceId}-groupTopic`);

        const newName = nameInputEl ? nameInputEl.value : this.channelData.name;
        const newTopic = topicTextareaEl ? topicTextareaEl.value : (this.channelData.topic || '');

        const updates = {};
        if (newName !== this.channelData.name) updates.name = newName;
        if (newTopic !== (this.channelData.topic || '')) updates.topic = newTopic;

        if (Object.keys(updates).length === 0) {
            this.appClient.notifier.info("No changes to save.");
            return;
        }

        try {
            await this.appClient.rpcClient.call('MessagingRpc.updateChannelInfo',
                { channelId: this.channelId, updates },
                { projectId: this.currentProjectId }
            );
            this.appClient.notifier.success("Group info updated.");
            await this.refreshModalData();
        } catch (error) {
            this.log.error("Error saving group info:", error);
            this.appClient.notifier.error("Failed to save group info.");
        }
    }

    async handleDeleteGroup() {
        const groupNameForConfirm = this.channelData?.name || 'this group';
        const confirmed = await this.appClient.notifier.confirm(
            this.t("Are you sure you want to delete the group \"{{groupName}}\"? This action cannot be undone.", { groupName: groupNameForConfirm }),
            {
                title: this.t('Confirm Delete Group'),
                type: 'danger',
                okButtonText: this.t('Delete Group'),
                cancelButtonText: this.t('Cancel')
            }
        );

        if (confirmed) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.deleteChannel',
                    { channelId: this.channelId },
                    { projectId: this.currentProjectId }
                );
                this.appClient.notifier.success(this.t("Group \"{{groupName}}\" deleted.", { groupName: groupNameForConfirm }));
                if (this.modalInstance) {
                    this.modalInstance.hide();
                }
            } catch (error) {
                this.log.error("Error deleting group:", error);
                this.appClient.notifier.error(this.t("Failed to delete group: {{errorMessage}}", { errorMessage: error.message }));
            }
        }
    }



    destroy() {
        this.avatarFileUpload?.destroy();
        if (this.modalElement) {
            this.modalInstance?.dispose();
            this.modalElement.remove();
        }
        super.destroy();
    }
}